import React, { ReactNode, useRef, useState } from 'react'
import { Div, Span, onActivate } from './html'
import { Loft, useDismissOnEvent } from './widgets'
import './system-config.css'

export type TSystemConfigTab = 'preferences' | 'midi' | 'devices' | 'backup'

export type TSystemConfigPort = {
    id: string
    name: string
    selected: boolean
    connected: boolean
}

export type TSystemConfigProps = {
    tab: TSystemConfigTab
    setTab: (tab: TSystemConfigTab) => void
    close: () => void
    offlineWarn: boolean
    toggleOfflineWarn: () => void
    ports: TSystemConfigPort[]
    togglePort: (id: string) => void
    clearPorts: () => void
    midiContent: ReactNode
    exportDevices: () => void
    importDevices: () => void
    saveBackup: () => void
    restoreBackup: () => void
}

const TABS: Array<{id: TSystemConfigTab, label: string}> = [
    {id: 'preferences', label: 'PREFERENCES'},
    {id: 'midi', label: 'MIDI'},
    {id: 'devices', label: 'DEVICES'},
    {id: 'backup', label: 'BACKUP'},
]

function ConfigAction ({
    children,
    danger,
    onAction,
}: {
    children: ReactNode
    danger?: boolean
    onAction: () => void
}) {
    return (
        <Div
            k={['cta', 'config-action', danger && 'danger']}
            role='button'
            tabIndex={0}
            {...onActivate(onAction)}
        >
            {children}
        </Div>
    )
}

function Preferences ({
    offlineWarn,
    toggleOfflineWarn,
}: Pick<TSystemConfigProps, 'offlineWarn' | 'toggleOfflineWarn'>) {
    return (
        <Div k='config-section'>
            <Div k='config-section-title'>GENERAL</Div>
            <Div k='config-help'>Warn when ProPatch is no longer responding.</Div>
            <Div k='config-setting'>
                <Div
                    k={['config-switch', offlineWarn && 'on']}
                    role='switch'
                    aria-checked={offlineWarn}
                    tabIndex={0}
                    {...onActivate(toggleOfflineWarn)}
                >
                    {offlineWarn && (
                        <svg
                            className='config-switch-check'
                            width='19'
                            height='15'
                            viewBox='0 0 19 15'
                            fill='none'
                            aria-hidden='true'
                        >
                            <path
                                d='M17.6906 2.48355L6.74918 13.425L6.39566 13.0714L1.0607 7.73746L0.707184 7.38297L1.0607 7.02945L2.13004 5.96109L2.48355 5.60757L6.7482 9.87222L15.5607 1.0607L15.9142 0.707184L17.6906 2.48355Z'
                                fill='#DBDCDE'
                                stroke='#DBDCDE'
                            />
                        </svg>
                    )}
                </Div>
                <Div k='config-setting-name'>Offline Warning Popup</Div>
            </Div>
        </Div>
    )
}

function MidiPort ({
    ports,
    togglePort,
    clearPorts,
}: Pick<TSystemConfigProps, 'ports' | 'togglePort' | 'clearPorts'>) {
    const [open, setOpen] = useState(false)
    const selectedPorts = ports.filter(port => port.selected)
    const selectedPortLabel = selectedPorts.length === 1
        ? selectedPorts[0].name
        : selectedPorts.length > 1
        ? `${selectedPorts.length} MIDI ports`
        : 'Select MIDI ports'

    return (
        <Div k='config-midi-port'>
            <Div k='config-midi-copy'>
                <Div k='config-section-title'>MIDI PORT</Div>
                <Div k='config-help'>Select a MIDI port to use with Pro Patch</Div>
            </Div>
            <Div k='config-midi-port-picker'>
                <Div
                    k='config-midi-port-select'
                    role='button'
                    aria-expanded={open}
                    aria-haspopup='listbox'
                    tabIndex={0}
                    {...onActivate(() => setOpen(!open))}
                >
                    {selectedPortLabel}
                    <svg
                        className='config-midi-port-chevron'
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        aria-hidden='true'
                    >
                        <mask id='config-midi-port-chevron-mask' style={{maskType: 'alpha'}} maskUnits='userSpaceOnUse' x='0' y='0' width='24' height='24'>
                            <rect width='24' height='24' fill='#D9D9D9'/>
                        </mask>
                        <g mask='url(#config-midi-port-chevron-mask)'>
                            <path d='M10.9995 16.0384L5.3457 10.3846L6.39953 9.33081L10.9995 13.9308L15.5995 9.33081L16.6534 10.3846L10.9995 16.0384Z' fill='#DBDCDE'/>
                        </g>
                    </svg>
                </Div>
                {open &&
                <Div k='config-midi-port-options' role='listbox' aria-label='MIDI ports'>
                        {ports.map(port => (
                            <Div
                                key={port.id}
                                k={['config-midi-port-option', port.selected && 'selected', !port.connected && 'disconnected']}
                                role='option'
                                aria-selected={port.selected}
                                tabIndex={0}
                                {...onActivate(() => togglePort(port.id))}
                            >
                                <Span k='symbols'>{port.selected ? 'check' : 'add'}</Span>
                                {port.name}
                            </Div>
                        ))}
                        <Div
                            k='config-midi-port-clear'
                            role='button'
                            tabIndex={0}
                            {...onActivate(() => {
                                clearPorts()
                                setOpen(false)
                            })}
                        >
                            CLEAR
                        </Div>
                    </Div>
                }
            </Div>
        </Div>
    )
}

function Devices ({
    exportDevices,
    importDevices,
}: Pick<TSystemConfigProps, 'exportDevices' | 'importDevices'>) {
    return (
        <Div k='config-section'>
            <Div k='config-section-title'>DEVICE CONFIGURATION</Div>
            <Div k='config-help'>Device configuration can be imported and exported via CSV. See this <a href='https://www.wolffaudio.com/support/propatch' target='_blank' rel='noreferrer'>documentation</a> for more information</Div>
            <Div k='config-actions'>
                <ConfigAction danger onAction={importDevices}>
                    IMPORT
                </ConfigAction>
                <ConfigAction onAction={exportDevices}>
                    EXPORT
                </ConfigAction>
            </Div>
        </Div>
    )
}

function Backup ({
    saveBackup,
    restoreBackup,
}: Pick<TSystemConfigProps, 'saveBackup' | 'restoreBackup'>) {
    return (
        <Div k='config-backup'>
            <Div k='config-section'>
                <Div k='config-section-title'>BACKUP</Div>
                <Div k='config-help'>Save the full workspace to one ProPatch backup file.</Div>
                <Div k='config-actions'>
                    <ConfigAction onAction={saveBackup}>SAVE BACKUP</ConfigAction>
                </Div>
            </Div>
            <Div k='config-section'>
                <Div k='config-section-title'>RESTORE</Div>
                <Div k='config-help'>Replace the full workspace with a ProPatch backup file.</Div>
                <Div k='config-actions'>
                    <ConfigAction danger onAction={restoreBackup}>RESTORE BACKUP</ConfigAction>
                </Div>
            </Div>
        </Div>
    )
}

export function SystemConfig (props: TSystemConfigProps) {
    const {tab, setTab, close} = props
    const ref = useRef<HTMLDivElement>(null)

    useDismissOnEvent({ref, clear: close, options: {blur: false, click: false}})

    const content = tab === 'preferences'
        ? <Preferences {...props}/>
        : tab === 'midi'
        ? <>
            <MidiPort {...props}/>
            {props.midiContent}
        </>
        : tab === 'devices'
        ? <Devices {...props}/>
        : <Backup {...props}/>

    return (
        <Loft target='root'>
            <Div
                k={['modal', 'system-config-modal']}
                style={{display: 'block'}}
                onPointerDown={e => {
                    if (e.target === e.currentTarget) close()
                }}
            >
                <Div
                    k={['dialog', 'system-config']}
                    ref={ref}
                    role='dialog'
                    aria-modal='true'
                    aria-label='System Configuration'
                >
                    <Div k='titlebar'>System Configuration
                        <Div k='dismiss' role='button' aria-label='Close' tabIndex={0} {...onActivate(close)}>close</Div>
                    </Div>
                    <Div k='system-config-main'>
                        <Div k='config-tabs' role='tablist' aria-label='Configuration sections'>
                            {TABS.map(item => (
                                <Div
                                    key={item.id}
                                    k={['config-tab', item.id === tab && 'selected']}
                                    role='tab'
                                    aria-selected={item.id === tab}
                                    data-config-tab={item.id}
                                    tabIndex={item.id === tab ? 0 : -1}
                                    {...onActivate(() => setTab(item.id))}
                                >
                                    {item.label}
                                </Div>
                            ))}
                        </Div>
                        <Div k={['system-config-body', tab === 'midi' && 'midi']}>
                            {content}
                        </Div>
                    </Div>
                </Div>
            </Div>
        </Loft>
    )
}
